import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { PropsWithChildren, useCallback, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { EntityCSVConfig } from '~hooks/useCSVImportExport/configs/types';
import { useCSVImportExport } from '~hooks/useCSVImportExport/useCSVImportExport';
import { useStores } from '~store';
import {
  loadSavedColumnMapping,
  matchColumnsToHeaders,
  saveColumnMapping,
} from '~utils/csv/csvColumnMapping';
import { CSVFileHeaders } from '~utils/csv/csvProcessor';

import {
  CSVColumnMapping,
  CSVColumnMappingFormValues,
  toHeaderOption,
} from './CSVColumnMapping';
import { CSVErrorReport } from './CSVErrorReport';
import { CSVFieldReference } from './CSVFieldReference';
import { CSVFileDropzone } from './CSVFileDropzone';
import { CSVImportButton } from './CSVImportButton';
import { CSVImportProgress } from './CSVImportProgress';
import { CSVParsePreview } from './CSVParsePreview';
import { CSVParseSummary } from './CSVParseSummary';
import getCsvImportInstructions from './getCsvImportInstructions';

type ModalState = 'initial' | 'mapping' | 'parsed' | 'importing' | 'complete';

// Diameter of the numbered step badge, in pixels, per the approved design.
const STEP_BADGE_SIZE = 28;

interface StepCardProps {
  stepNumber: number;
  title: string;
  description: string;
  action?: React.ReactNode;
}

function StepCard({
  stepNumber,
  title,
  description,
  action,
  children,
}: PropsWithChildren<StepCardProps>) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2.5,
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start',
      }}
    >
      <Box
        sx={{
          width: STEP_BADGE_SIZE,
          height: STEP_BADGE_SIZE,
          minWidth: STEP_BADGE_SIZE,
          borderRadius: '50%',
          backgroundColor: 'brandV2Black.main',
          color: 'brandV2Black.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600,
          fontSize: 14,
        }}
      >
        {stepNumber}
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          {description}
        </Typography>
        {children}
      </Box>

      {action && <Box sx={{ alignSelf: 'center' }}>{action}</Box>}
    </Box>
  );
}

interface CSVImportExportModalProps<T, TExport = T> {
  isOpen: boolean;
  onClose: () => void;
  config: EntityCSVConfig<T, TExport>;
  loadConfig?: () => Promise<EntityCSVConfig<T, TExport>>;
  companyId: string;
  entityDisplayName: string;
  onImportComplete?: () => void;
}

export function CSVImportExportModal<T, TExport = T>({
  isOpen,
  onClose,
  config,
  loadConfig,
  companyId,
  entityDisplayName,
  onImportComplete,
}: CSVImportExportModalProps<T, TExport>) {
  const { toasterStore } = useStores();
  const [modalState, setModalState] = useState<ModalState>('initial');
  const [mappingFile, setMappingFile] = useState<{
    file: File;
    fileHeaders: CSVFileHeaders;
  } | null>(null);
  const {
    control: columnMappingControl,
    handleSubmit: handleColumnMappingSubmit,
    reset: resetColumnMapping,
  } = useForm<CSVColumnMappingFormValues>();
  const selectedHeaderOptions = useWatch({ control: columnMappingControl });
  const requiredColumnsAreMapped = config.columns.every(
    (column) => !column.required || selectedHeaderOptions[column.key],
  );
  const supportsExport = Boolean(config.fetchAll && config.transformExport);

  const {
    fileSelectionId,
    importConfig,
    isExporting,
    isImporting,
    isLoading,
    progress,
    parseResult,
    importResult,
    selectedFileName,
    downloadTemplate,
    exportAll,
    parseFile,
    readFileHeaders,
    importData,
    downloadFailedRows,
    reset,
  } = useCSVImportExport({
    config,
    companyId,
    loadConfig,
    onSuccess: (result) => {
      const failedCount = result.failed.length + (parseResult?.invalidRows.length ?? 0);
      toasterStore.push({
        message: failedCount
          ? t('csv_import_export.import_complete_with_failures', {
              failed: failedCount,
              succeeded: result.succeeded.length,
            })
          : t('csv_import_export.import_complete'),
        kind: failedCount ? 'warn' : 'success',
        display: true,
        autoHideDuration: 6000,
      });
      onImportComplete?.();
    },
    onError: (error) => {
      toasterStore.push({
        message: error.message,
        kind: 'error',
        display: true,
        autoHideDuration: 6000,
      });
    },
  });

  const handleClose = useCallback(() => {
    reset();
    setMappingFile(null);
    setModalState('initial');
    onClose();
  }, [reset, onClose]);

  const handleFileSelect = useCallback(
    async (file: File) => {
      if (!config.confirmsColumnMapping) {
        if (await parseFile(file)) {
          setModalState('parsed');
        }
        return;
      }

      const fileHeaders = await readFileHeaders(file);
      if (!fileHeaders) return;

      const columnMapping =
        loadSavedColumnMapping(config.entityType, config.columns, fileHeaders.headers) ??
        matchColumnsToHeaders(config.columns, fileHeaders.headers);

      resetColumnMapping(
        Object.fromEntries(
          config.columns.map((column) => {
            const header = columnMapping[column.key];
            return [column.key, header ? toHeaderOption(header) : null];
          }),
        ),
      );
      setMappingFile({ file, fileHeaders });
      setModalState('mapping');
    },
    [config, parseFile, readFileHeaders, resetColumnMapping],
  );

  const handleConfirmMapping = handleColumnMappingSubmit(async (selectedOptions) => {
    if (!mappingFile) return;

    const selectedHeaders = Object.entries(selectedOptions).map(
      ([columnKey, option]) => [columnKey, option?.value ?? null] as const,
    );
    saveColumnMapping(
      config.entityType,
      mappingFile.fileHeaders.headers,
      Object.fromEntries(selectedHeaders),
    );
    const columnMapping = Object.fromEntries(
      selectedHeaders.filter((entry): entry is [string, string] => entry[1] !== null),
    );

    if (await parseFile(mappingFile.file, columnMapping)) {
      setModalState('parsed');
    }
  });

  const handleImport = useCallback(async () => {
    if (!parseResult?.validRows.length) return;

    setModalState('importing');
    await importData(parseResult.validRows);
    setModalState('complete');
  }, [parseResult, importData]);

  const handleBackToInitial = useCallback(() => {
    reset();
    setMappingFile(null);
    setModalState('initial');
  }, [reset]);

  const handleBackFromParsed = useCallback(() => {
    if (!mappingFile) {
      handleBackToInitial();
      return;
    }
    reset();
    setModalState('mapping');
  }, [mappingFile, reset, handleBackToInitial]);

  const renderInitialState = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <StepCard
        action={
          <SmallLoadingButton
            variant="outlined"
            color="brandV2Black"
            // brandV2Black is a fixed navy (invisible on the dark-mode surface);
            // flipping tokens keep the outlined button readable in both modes.
            sx={{ color: 'text.primary', borderColor: 'divider' }}
            startIcon={<DescriptionOutlined />}
            onClick={downloadTemplate}
          >
            {t('csv_import_export.download_template')}
          </SmallLoadingButton>
        }
        description={t('csv_import_export.steps.download.description')}
        stepNumber={1}
        title={t('csv_import_export.steps.download.title')}
      />

      <StepCard
        description={getCsvImportInstructions(config, entityDisplayName)}
        stepNumber={2}
        title={t('csv_import_export.steps.fill.title')}
      >
        <CSVFieldReference columns={config.columns} entityType={config.entityType} />
      </StepCard>

      <StepCard
        description={t('csv_import_export.steps.upload.description')}
        stepNumber={3}
        title={t('csv_import_export.steps.upload.title')}
      >
        <CSVFileDropzone
          disabled={isLoading}
          fileSelectionId={fileSelectionId}
          onClear={reset}
          onSelect={handleFileSelect}
          selectedFileName={selectedFileName}
        />
      </StepCard>
    </Box>
  );

  const renderMappingState = () =>
    mappingFile && (
      <CSVColumnMapping
        columns={config.columns}
        control={columnMappingControl}
        fileHeaders={mappingFile.fileHeaders}
        fileName={mappingFile.file.name}
      />
    );

  const renderParsedState = () =>
    parseResult && (
      <>
        <CSVParseSummary parseResult={parseResult} sx={{ mb: 2 }} />
        <CSVParsePreview columns={config.columns} parseResult={parseResult} />
      </>
    );

  const renderImportingState = () =>
    progress && <CSVImportProgress progress={progress} />;

  const renderCompleteState = () => (
    <CSVErrorReport
      parseResult={parseResult}
      importResult={importResult}
      resultSummary={
        importResult ? importConfig.summarizeResults?.(importResult.succeeded) : null
      }
      rowLabel={importConfig.rowLabel}
      onDownloadFailedRows={downloadFailedRows}
      onClose={handleClose}
    />
  );

  const renderContent = () => {
    switch (modalState) {
      case 'initial':
        return renderInitialState();
      case 'mapping':
        return renderMappingState();
      case 'parsed':
        return renderParsedState();
      case 'importing':
        return renderImportingState();
      case 'complete':
        return renderCompleteState();
      default:
        throw new Error(`Unsupported CSV modal state: ${modalState}`);
    }
  };

  const renderImportButton = () => (
    <CSVImportButton
      entityDisplayName={entityDisplayName}
      isImporting={isImporting}
      onImport={handleImport}
      validRowCount={parseResult?.validRows.length ?? 0}
    />
  );

  const renderActions = () => {
    if (modalState === 'initial') {
      return (
        <>
          {supportsExport && (
            <SmallLoadingButton
              variant="contained"
              color="brandV2Black"
              startIcon={<FileDownloadOutlined />}
              onClick={exportAll}
              loading={isExporting}
              sx={{ mr: 'auto' }}
            >
              {t('csv_import_export.export_all_entity', {
                entity: entityDisplayName.toLowerCase(),
              })}
              <Typography
                component="span"
                variant="caption"
                sx={{ ml: 0.5, color: 'inherit', opacity: 0.7 }}
              >
                {t('csv_import_export.csv_extension')}
              </Typography>
            </SmallLoadingButton>
          )}
          <SmallButton variant="outlined" onClick={handleClose}>
            {t('actions.cancel')}
          </SmallButton>
          {renderImportButton()}
        </>
      );
    }

    if (modalState === 'mapping') {
      return (
        <>
          <SmallButton
            disabled={isLoading}
            variant="outlined"
            onClick={handleBackToInitial}
          >
            {t('actions.back')}
          </SmallButton>
          <SmallLoadingButton
            color="brandV2Yellow"
            disabled={!requiredColumnsAreMapped}
            loading={isLoading}
            onClick={handleConfirmMapping}
            variant="contained"
          >
            {t('csv_import_export.column_mapping.confirm')}
          </SmallLoadingButton>
        </>
      );
    }

    if (modalState === 'parsed') {
      return (
        <>
          <SmallButton variant="outlined" onClick={handleBackFromParsed}>
            {t('actions.back')}
          </SmallButton>
          {renderImportButton()}
        </>
      );
    }

    if (modalState === 'importing') {
      return (
        <SmallButton variant="outlined" disabled>
          {t('csv_import_export.importing')}
        </SmallButton>
      );
    }

    // 'complete' state actions are handled by CSVErrorReport
    return null;
  };

  return (
    <Dialog isOpen={isOpen} onClose={handleClose} maxWidth="lg">
      <DialogHeader onClose={handleClose}>
        {supportsExport
          ? t('csv_import_export.title', { entity: entityDisplayName })
          : t('csv_import_export.import_title', { entity: entityDisplayName })}
      </DialogHeader>

      <DialogContent>{renderContent()}</DialogContent>

      {modalState !== 'complete' && <DialogActions>{renderActions()}</DialogActions>}
    </Dialog>
  );
}
